import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal, Button, Form } from "react-bootstrap";
import createCustomSchema from "../../fieldValidator/fieldValidator";
import { calculateResignationDates } from "../../utils/calculateResignationDates";
import { employeeDetails } from "../../auth";
import api from "../../api/axios";
import Swal from "sweetalert2";
import { terminateEmployee } from "../../services/ResignationServices";
import Loader from "../Loader";

const TerminationModal = ({ show, handleClose, selectedEmployee }) => {
  const [details, setDetails] = useState({
    employee_id: "",
    employee: "",
    resignation_date: null,
    last_working_time: null,
    reason_of_resignation: null,
    last_working_date: null,
    notice_period_days: "",
  });

  const [errors, setErrors] = useState({});
  const [resignationPolicyDetails, setResignationPolicyDetails] = useState({});
  const [loader, setLoader] = useState(false);
  const loginEmployeeDetails = employeeDetails();

  const closeModal = () => {
    handleReset();
    handleClose();
  };
  const company_id = loginEmployeeDetails?.company.company_id;

  const validationSchema = {
    resignation_date: createCustomSchema("date", {
      message: "Termination date is required",
    }),
    notice_period_days: createCustomSchema("number", {
      message: "Notice period is required",
      min: 0,
      max: 365,
    }),
    last_working_date: createCustomSchema("date", {
      message: "Last working date is required",
    }),
    last_working_time: createCustomSchema("date", {
      message: "Last working time is required",
    }),
    reason_of_resignation: createCustomSchema("paragraph", {
      message: "Reason is required",
      min: 10,
    }),
  };

  const validateField = async (key, value) => {
    try {
      await validationSchema[key].validate(value);
      setErrors((prevErrors) => ({ ...prevErrors, [key]: null }));
    } catch (error) {
      setErrors((prevErrors) => ({ ...prevErrors, [key]: error.message }));
    }
  };

  const fetchResignationPolicyData = (id, employment_status , department_id) => {
    setLoader(true);
    api
      .get(`resignation-policies?company_id=${id}&employment_status=${employment_status}&department_id=${department_id}`)
      .then((res) => {
        setResignationPolicyDetails(res?.data?.result[0]);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (show) {
      console.log("selectedEmployee" , selectedEmployee);
      const updatedDate = new Date();
      setDetails({
        employee_id: selectedEmployee?.id ?? "",
        employee: `${selectedEmployee?.first_name} ${
          selectedEmployee?.middle_name ?? ""
        } ${selectedEmployee?.last_name}`,
        resignation_date: new Date(),
        reason_of_resignation: null,
        last_working_date: new Date(),
        last_working_time: updatedDate ?? "",
        notice_period_days: "",
      });

      fetchResignationPolicyData(company_id , selectedEmployee?.employment_status , selectedEmployee?.department_id
);
    }
  }, [show]);

  useEffect(() => {
    if (details.last_working_date && details.resignation_date) {
      const noticePeriod = Math.ceil(
        (details.last_working_date - details.resignation_date) /
          (1000 * 60 * 60 * 24)
      );
      setDetails((prevDetails) => ({
        ...prevDetails,
        notice_period_days: noticePeriod,
      }));
      validateField("notice_period_days", noticePeriod);
    }
  }, [details.resignation_date, details.last_working_date]);

  const handleChange = (key, value) => {
    setDetails((prevDetails) => ({
      ...prevDetails,
      [key]: value,
    }));
    validateField(key, value);
  };

  const handleReset = () => {
    setDetails((prevDetails) => ({
      ...prevDetails,
      resignation_date: null,
      reason_of_resignation: "",
      last_working_date: null,
      last_working_time: null,
      notice_period_days: "",
    }));
    setErrors({});
  };

  const checkValidations = async () => {
    const newErrors = {};
    let hasError = false;

    await Promise.all(
      Object.keys(validationSchema).map(async (key) => {
        try {
          await validationSchema[key].validate(details[key]);
          newErrors[key] = null;
        } catch (error) {
          newErrors[key] = error.message;
          hasError = true;
        }
      })
    );

    setErrors(newErrors);
    return !hasError;
  };

  const handleSubmit = async () => {
    setLoader(true);
    try {
      const hasError = await checkValidations();
      if (!hasError) return;

      const resignationDates = await calculateResignationDates(
        resignationPolicyDetails,
        details
      );

      const {
        salary_release_date,
        asset_return_due_date,
        pending_amount_release_date,
        document_release_date,
      } = resignationDates;

      const employeeResignation = {
        terminate_by: loginEmployeeDetails?.id,
        employee_id: details?.employee_id,
        notice_period_days: details?.notice_period_days,
        resignation_date: details?.resignation_date,
        last_working_date: details?.last_working_date,
        last_working_time: details?.last_working_time,
        salary_hold_type_id: 1,
        salary_release_date,
        asset_return_status: "Pending",
        asset_return_due_date,
        pending_amount_release_date,
        document_release_date,
        reason_of_resignation: details?.reason_of_resignation,
      };

      const response = await terminateEmployee(employeeResignation);
      closeModal();
      Swal.fire({
        title: "Termination",
        text: response?.message,
        icon: "success",
        confirmButtonText: "OK",
        timer: 1000,
        timerProgressBar: true,
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    } catch (error) {
      closeModal();
      Swal.fire({
        title: "Termination",
        text: "Something went wrong while processing.",
        icon: "warning",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "btn btn-primary",
        },
      });
    } finally {
      closeModal();
      setLoader(false);
    }
  };

  return (
    <Modal show={show} centered backdrop="static">
      <Modal.Header>
        <Modal.Title>Terminate {details?.employee}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Termination Date <span className="text-danger">*</span>
            </Form.Label>
            <div className="cal-icon">
              <DatePicker
                selected={details.resignation_date}
                onChange={(date) => handleChange("resignation_date", date)}
                className="form-control floating datetimepicker"
                placeholderText="Select date"
                type="date"
                dateFormat="dd-MM-yyyy"
                maxDate={details?.last_working_date}
              />
              {errors.resignation_date && (
                <Form.Text className="text-danger">
                  * {errors.resignation_date}
                </Form.Text>
              )}
            </div>
          </Form.Group>
          <div className="d-flex justify-content-between gap-2">
            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center justify-content-between">
                <span>
                  Last working Date <span className="text-danger">*</span>
                </span>
              </Form.Label>
              <div className="cal-icon">
                <DatePicker
                  selected={details.last_working_date}
                  onChange={(date) => handleChange("last_working_date", date)}
                  className="form-control floating datetimepicker"
                  placeholderText="Select date"
                  type="date"
                  dateFormat="dd-MM-yyyy"
                  minDate={details.resignation_date}
                />
                {errors.last_working_date && (
                  <Form.Text className="text-danger">
                    * {errors.last_working_date}
                  </Form.Text>
                )}
              </div>
              <Form.Text
                className="form-control-plaintext text-dark m-0 p-0 pt-2"
                style={{ fontSize: "1rem" }}
              >
                {details.last_working_date && details.resignation_date ? (
                  <>
                    <span>Notice period: </span>
                    {details?.notice_period_days} days
                    {errors.notice_period_days ? (
                      <span className="text-danger">
                        * {errors.notice_period_days}
                      </span>
                    ) : (
                      ""
                    )}
                  </>
                ) : (
                  ""
                )}
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                Time <span className="text-danger">*</span>
              </Form.Label>
              <div className="cal-icon">
                <DatePicker
                  selected={details?.last_working_time}
                  onChange={(time) => {
                    handleChange("last_working_time", time);
                  }}
                  className="form-control floating datetimepicker"
                  showTimeSelectOnly
                  placeholderText="Select time"
                  type="datetime"
                  showTimeSelect
                  timeFormat="HH:mm a"
                  timeCaption="Time"
                  timeIntervals={15}
                  dateFormat="HH:mm a"
                />
                {errors.last_working_time && (
                  <Form.Text className="text-danger">
                    * {errors.last_working_time}
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </div>
          <Form.Group className="mb-3">
            <Form.Label>
              Reason <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={details.reason_of_resignation}
              onChange={(e) =>
                handleChange("reason_of_resignation", e.target.value)
              }
            />
            {errors.reason_of_resignation && (
              <Form.Text className="text-danger">
                * {errors.reason_of_resignation}
              </Form.Text>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal} disabled={loader}>
          Close
        </Button>
        <Button variant="warning" onClick={handleReset} disabled={loader}>
          Reset
        </Button>
        <Button
          variant="danger"
          type="submit"
          onClick={handleSubmit}
          disabled={loader}
        >
          {loader ? "Terminating" : "Terminate"}
        </Button>
      </Modal.Footer>
      <Loader show={loader} />
    </Modal>
  );
};

export default TerminationModal;
